import type {
  EmployerDataFilter,
  ReportEmployer,
  ReportEmployerConnection,
  ReportSubOwner,
  SubOwnerDataFilter,
  UserDataFilterResponse,
} from 'app/types/report.types';

const toReportEmployer = (employer: EmployerDataFilter): ReportEmployer => ({
  ...employer,
  connections: employer.connections || [],
  subEmployers: employer.subEmployers.map(toReportEmployer),
  eventFlowDefaultUser: employer.eventFlowDefaultUser || undefined,
});

const flattenEmployers = (employers: Array<EmployerDataFilter>) => {
  return employers.reduce<Array<ReportEmployer>>((acc, employer) => {
    const reportEmployer = toReportEmployer(employer);
    acc.push(reportEmployer);

    if (reportEmployer.subEmployers?.length) {
      reportEmployer.subEmployers.forEach(subEmployer => {
        acc.push(subEmployer);
      });
    }

    return acc;
  }, []);
};

const sortAscByName = (
  elementA: ReportEmployer | ReportEmployerConnection,
  elementB: ReportEmployer | ReportEmployerConnection,
) => {
  if (elementA.name < elementB.name) {
    return -1;
  }
  if (elementA.name > elementB.name) {
    return 1;
  }
  return 0;
};

export const normalizeReportData = (data: UserDataFilterResponse | undefined) => {
  const reportData: Array<ReportEmployer> = [];

  data?.results.forEach(result => {
    const employers = 'subOwnerId' in result ? result.employers : [result];
    reportData.push(...flattenEmployers(employers));
  });

  return reportData
    .map(employer => {
      return { ...employer, connections: [...employer.connections].sort(sortAscByName) };
    })
    .sort(sortAscByName);
};

export const getReportSubOwners = (data: UserDataFilterResponse | undefined): Array<ReportSubOwner> =>
  data?.results
    .filter((result): result is SubOwnerDataFilter => 'subOwnerId' in result)
    .map(subOwner => ({
      ...subOwner,
      name: subOwner.name || '',
      employers: subOwner.employers.map(toReportEmployer),
    })) || [];
